import api from '@/services/api';
import { ChargeException } from '@/types/charge-exception';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enrollmentKeys } from './use-enrollments';
import { chargeKeys } from './use-charges';

export interface ChargeExceptionPayload {
  originalChargeDate: string;
  action: ChargeException['action'];
  newDueDate?: string;
  newAmount?: number;
  reason: string;
}

export function useCreateChargeException(enrollmentId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ChargeExceptionPayload) => {
      if (!enrollmentId) throw new Error('Enrollment ID is required');
      const response = await api.post<ChargeException>(
        `/charge-exception/${enrollmentId}`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      if (!enrollmentId) return;
      queryClient.invalidateQueries({
        queryKey: enrollmentKeys.detail(enrollmentId),
      });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.provider() });
      queryClient.invalidateQueries({ queryKey: chargeKeys.byEnrollment(enrollmentId) });
    },
  });
}

export function useUpdateChargeException(enrollmentId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<ChargeExceptionPayload>;
    }) => {
      const response = await api.patch<ChargeException>(`/charge-exception/${id}`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: enrollmentKeys.detail(enrollmentId || data.id),
        });
      }
      if (enrollmentId) {
        queryClient.invalidateQueries({ queryKey: enrollmentKeys.provider() });
        queryClient.invalidateQueries({ queryKey: chargeKeys.byEnrollment(enrollmentId) });
      }
    },
  });
}

export function useDeleteChargeException(enrollmentId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<ChargeException>(`/charge-exception/${id}`);
      return response.data;
    },
    onSuccess: () => {
      if (enrollmentId) {
        queryClient.invalidateQueries({
          queryKey: enrollmentKeys.detail(enrollmentId),
        });
        queryClient.invalidateQueries({ queryKey: enrollmentKeys.provider() });
        queryClient.invalidateQueries({ queryKey: chargeKeys.byEnrollment(enrollmentId) });
      }
    },
  });
}
